import { makeAutoObservable } from 'mobx';
import { TRPCPost } from '@/infrastructure/axios';
import { API_PREFIX } from '@/infrastructure/axios/code';
import { getProductId } from '@/infrastructure/product-info';
import { TimeRange } from '@/infrastructure/utils/timestamp';
import {
  Field,
  FIELD_ALL,
  FieldKey,
  FieldScope,
  Option,
  Zone,
  EXIT_REASON_OPTION,
} from '@/proto/field';
import { IFilter, IGetFieldDistinctDataReq, IGetFieldDistinctDataRsp, MetricType } from '@/proto/metric';
import { basicService } from '@/service/basic';
import { toPBTime } from '@raft/utils/src/date';


export class FieldStore {
  fields: Field = {};

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  public getField(fieldKey: FieldKey, scope: FieldScope) {
    const key = this.getKey(fieldKey, scope);
    return this.formatField(fieldKey, this.fields[key]);
  }

  public async loadField(fieldKey: FieldKey, scope: FieldScope, filter?: IFilter[], unshiftAll = true) {
    const key = this.getKey(fieldKey, scope);
    if (!this.fields[key]) {
      this.fields[key] = await this.load(fieldKey, scope, undefined, filter);
    }
    return this.formatField(fieldKey, this.fields[key], unshiftAll);
  }

  public async updateField(fieldKey: FieldKey, scope: FieldScope) {
    const key = this.getKey(fieldKey, scope);
    const field = await this.load(fieldKey, scope);
    this.fields = {
      ...this.fields,
      [key]: field,
    };
  }

  public async load(
    field: FieldKey,
    scope: FieldScope,
    timeRange?: TimeRange,
    filter?: IFilter[],
  ): Promise<string[]> {
    if (typeof scope === 'string'
      && Object.values(Zone).includes(scope as Zone)) {
      return this.loadWebServerField(field, scope as Zone, timeRange, filter);
    }
    if (typeof scope === 'number'
      && Object.values(MetricType).includes(scope)) {
      return this.loadMetricServerField(field, scope as MetricType, timeRange);
    }
    return [];
  }

  public formatField(fieldKey: FieldKey, fields?: string[], unshiftAll = true): Option[] {
    let allEntry = unshiftAll ? [{ value: FIELD_ALL, label: '所有' }] : [];
    let fieldEntry;

    if (fieldKey === FieldKey.LaunchTag) {
      allEntry = [];
    }

    if (fieldKey === FieldKey.ExitReason) {
      fieldEntry = fields?.map(field => ({
        label: EXIT_REASON_OPTION.find(option => option.value === field)?.label ?? field,
        value: field,
      })) ?? [];
    } else {
      fieldEntry = fields?.map(field => ({
        label: field,
        value: field,
      })) ?? [];
    }

    return [...allEntry, ...fieldEntry];
  }

  private getKey(fieldKey: FieldKey, scope: FieldScope) {
    return `${getProductId()}_${fieldKey}_${scope}`;
  }

  private async loadWebServerField(
    field: FieldKey,
    zone: Zone,
    timeRange?: TimeRange,
    filter?: IFilter[],
  ): Promise<string[]> {
    try {
      const resp = await basicService.getMonitorFieldDistinctData({
        productID: getProductId(),
        dim: field,
        type: zone,
        endTime: timeRange ? timeRange[1].endOf('date')
          .unix() : null,
        startTime: timeRange ? timeRange[0].startOf('date')
          .unix() : null,
        optionalFilters: filter,
        limit: 5000,
      });
      return resp;
    } catch (e) {
      return [];
    }
  }

  private async loadMetricServerField(
    field: FieldKey,
    type: MetricType,
    timeRange?: TimeRange,
  ): Promise<string[]> {
    try {
      const reqBody: IGetFieldDistinctDataReq = {
        productId: getProductId(),
        name: field,
        type,
        endTime: timeRange ? toPBTime(timeRange[1].endOf('date')) : null,
        startTime: timeRange ? toPBTime(timeRange[0].startOf('date')) : null,
      };
      const resp = await TRPCPost<IGetFieldDistinctDataRsp>(
        `/${API_PREFIX}/trpc/trpc.rmonitor.metrics_server.GetMetrics/GetFieldDistinctData`,
        reqBody,
      );
      return resp.values ?? [];
    } catch (e) {
      return [];
    }
  }
}
